import PropTypes from 'prop-types'
import { Switch } from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'
import { createUseStyles } from 'react-jss'

// Width of antd's expand gutter column. The expanded details are indented by it
// so the feature toggles line up with the card above them.
export const EXPAND_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  lineIcon: {
    marginRight: 6,
    color: '#8c8c8c',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    // The switch is the control the admin came here for, so give it room to
    // breathe under its label instead of the tight text-only spacing.
    gap: 4,
    alignItems: 'flex-start',
  },
  fieldLabel: {
    fontSize: 11,
    color: '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
})

// Returns the labels of every flag currently attached to this practice, in the
// same order the desktop columns render them.
function enabledLabelsFor(practice, featureFlags) {
  return featureFlags
    .filter(flag => flag.practices?.some(p => p.id === practice.id))
    .map(flag => flag.label)
}

export function PracticeFeaturesMobileCard({ practice, featureFlags }) {
  const classes = useStyles()
  const enabledLabels = enabledLabelsFor(practice, featureFlags)
  const total = featureFlags.length
  const summary =
    total === 0
      ? 'No feature flags'
      : `${enabledLabels.length} of ${total} feature${
          total === 1 ? '' : 's'
        } enabled`

  return (
    <div className={classes.card}>
      <div className={classes.name}>{practice.name || '—'}</div>
      <div className={classes.line}>{summary}</div>
      {Boolean(enabledLabels.length) && (
        <div className={classes.line}>
          <CheckCircleOutlined className={classes.lineIcon} />
          {enabledLabels.join(' · ')}
        </div>
      )}
    </div>
  )
}

PracticeFeaturesMobileCard.propTypes = {
  practice: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  }).isRequired,
  featureFlags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      practices: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
    }),
  ).isRequired,
}

// One switch per feature flag, mirroring the dynamic desktop columns. Kept
// uncontrolled (`defaultChecked`) exactly like the desktop table: the panel is
// remounted every time the row is expanded, so it always picks up fresh state.
export function PracticeFeaturesMobileDetails({
  practice,
  featureFlags,
  loading,
  onToggle,
  indent = 0,
}) {
  const classes = useStyles()

  return (
    <div className={classes.detailsGrid} style={{ paddingLeft: indent }}>
      {featureFlags.map(flag => (
        <div className={classes.field} key={flag.id}>
          <span className={classes.fieldLabel}>{flag.label}</span>
          <span className={classes.fieldValue}>
            <Switch
              onChange={checked => onToggle(practice.id, flag.id, checked)}
              loading={loading === `toggle-${practice.id}-${flag.id}`}
              defaultChecked={flag.practices?.some(p => p.id === practice.id)}
            />
          </span>
        </div>
      ))}
    </div>
  )
}

PracticeFeaturesMobileDetails.propTypes = {
  practice: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  featureFlags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      practices: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
    }),
  ).isRequired,
  loading: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  indent: PropTypes.number,
}
